import { useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import type Ouvrage from "models/Ouvrage";
import type User from "models/User";
import type Commune from "models/Commune";
import graphService from "services/graphService";
import ouvrageService from "services/ouvrageService";
import territoireService from "services/territoireService";
import communeService from "services/communeService";
import { useSnackbar } from "notistack";
import ToastMessages from "constants/ToastMessages";
import DataContext from "contexts/DataContext";
import type { IData } from "contexts/DataContext";
import type { ControlledTextFieldOptions } from "components/inputs/ControlledTextField";

function DataProvider({ children }: Readonly<PropsWithChildren>): ReactNode {
  const [communes, setCommunes] = useState<Array<Commune>>();
  const [territoires, setTerritoires] = useState<ControlledTextFieldOptions>();
  const [ouvrages, setOuvrages] = useState<Array<Ouvrage>>();
  const [agents, setAgents] = useState<Array<User>>();
  const [error, setError] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (error) {
      enqueueSnackbar(ToastMessages.ERROR_RETRY, {
        variant: "error",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    async function fetchAgents(): Promise<void> {
      try {
        const agents = await graphService.getAllAgents([
          "department",
          "city",
          "manager",
          "employeeId",
        ]);
        agents?.sort((a: User, b: User) => a.displayName.localeCompare(b.displayName, "fr"));
        setAgents(agents);
      } catch (error: unknown) {
        console.error("Problem fetching Inital Data : Agents : ", error);
        setError(true);
      }
    }

    async function fetchCommunes(): Promise<void> {
      try {
        const communes = await communeService.getAllCommunes();
        communes.sort((a: Commune, b: Commune) => a.nom.localeCompare(b.nom, "fr"));
        setCommunes(communes);
      } catch (error: unknown) {
        console.error("Problem fetching Inital Data : Communes : ", error);
        setError(true);
      }
    }

    async function fetchTerritoires(): Promise<void> {
      try {
        const territoires = await territoireService.getAllTextFieldOptions();
        setTerritoires(territoires);
      } catch (error: unknown) {
        console.error("Problem fetching Inital Data : Territoires : ", error);
        setError(true);
      }
    }

    async function fetchOuvrages(): Promise<void> {
      try {
        const ouvrages = await ouvrageService.getAll();
        setOuvrages(ouvrages);
      } catch (error: unknown) {
        console.error("Problem fetching Inital Data : Ouvrages : ", error);
        setError(true);
      }
    }

    void fetchAgents();
    void fetchCommunes();
    void fetchTerritoires();
    void fetchOuvrages();
  }, []);

  const isLoading = useMemo(
    () =>
      communes === undefined &&
      territoires === undefined &&
      ouvrages === undefined &&
      agents === undefined,
    [communes, territoires, ouvrages, agents]
  );

  const data: IData = useMemo(
    () => ({
      isLoading,
      communes: communes ?? [],
      territoires: territoires ?? [],
      ouvrages: ouvrages ?? [],
      agents: agents ?? [],
      error,
    }),
    [isLoading, communes, territoires, ouvrages, agents, error]
  );

  return <DataContext.Provider value={data}>{children}</DataContext.Provider>;
}

function useData(): IData {
  const context = useContext(DataContext);

  if (context == null) {
    throw new Error("useData must be used within a DataProvider");
  }

  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export { DataProvider, useData };
